import { useCallback, useEffect, useState } from 'react';
import type { FavoriteSite } from '../types';

const STORAGE_KEY = 'aiprompt:favorites:v1';

// SecureMyName is pinned first (Dan's product). The remaining are
// alphabetised. Domain registrars use {domain} (strips spaces, lowercases,
// appends .com when there's no TLD). AI chat tools and search engines use
// {keyword} (literal value, URL-encoded).
const DEFAULT_FAVORITES: FavoriteSite[] = [
  {
    id: 'securemyname',
    name: 'SecureMyName',
    template: 'https://app.securemyname.com/search?domain={domain}',
  },
  {
    id: 'chatgpt',
    name: 'ChatGPT',
    template: 'https://chatgpt.com/?q={keyword}',
  },
  {
    id: 'claude-chat',
    name: 'Claude Chat',
    template: 'https://claude.ai/new?q={keyword}',
  },
  {
    id: 'claude-code',
    name: 'Claude Code',
    template: 'https://claude.ai/code/new?q={keyword}',
  },
  {
    id: 'dynadot',
    name: 'Dynadot',
    template: 'https://www.dynadot.com/domain/search?domain={domain}',
  },
  {
    id: 'godaddy',
    name: 'GoDaddy',
    template: 'https://www.godaddy.com/domainsearch/find?domainToCheck={domain}',
  },
  {
    id: 'gemini',
    name: 'Google Gemini',
    template: 'https://gemini.google.com/app?q={keyword}',
  },
  {
    id: 'google',
    name: 'Google Search',
    template: 'https://www.google.com/search?q={keyword}',
  },
  {
    id: 'grok',
    name: 'Grok',
    template: 'https://grok.com/?q={keyword}',
  },
  {
    id: 'namecheap',
    name: 'Namecheap',
    template: 'https://www.namecheap.com/domains/registration/results/?domain={domain}',
  },
  {
    id: 'perplexity',
    name: 'Perplexity',
    template: 'https://www.perplexity.ai/search?q={keyword}',
  },
  {
    id: 'spaceship',
    name: 'Spaceship',
    template: 'https://www.spaceship.com/domain-search/?query={domain}&tab=domains',
  },
  {
    id: 'unstoppable',
    name: 'Unstoppable Domains',
    template: 'https://unstoppabledomains.com/search?searchTerm={domain}',
  },
  {
    id: 'x',
    name: 'X (post)',
    // intent/post is the official pre-fill URL — opens X's compose tab
    // with the value already typed. /compose/post by itself opens compose
    // but doesn't accept a text query param, so we'd lose the whole point
    // of the favorites pattern.
    template: 'https://x.com/intent/post?text={keyword}',
  },
];

function loadFavorites(): FavoriteSite[] {
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    if (!raw) return DEFAULT_FAVORITES;
    const parsed = JSON.parse(raw) as FavoriteSite[];
    if (!Array.isArray(parsed) || parsed.length === 0) return DEFAULT_FAVORITES;
    return parsed.filter((f) => f && f.name && f.template);
  } catch {
    return DEFAULT_FAVORITES;
  }
}

function saveFavorites(favorites: FavoriteSite[]) {
  try {
    localStorage.setItem(STORAGE_KEY, JSON.stringify(favorites));
  } catch {
    // localStorage unavailable; silently ignore
  }
}

function makeId() {
  return Math.random().toString(36).slice(2, 10);
}

export function useFavorites() {
  const [favorites, setFavorites] = useState<FavoriteSite[]>(() => loadFavorites());

  useEffect(() => {
    saveFavorites(favorites);
  }, [favorites]);

  const addFavorite = useCallback((name: string, template: string) => {
    if (!name.trim() || !template.trim()) return;
    setFavorites((prev) => [
      ...prev,
      { id: makeId(), name: name.trim(), template: template.trim() },
    ]);
  }, []);

  const updateFavorite = useCallback((id: string, patch: Partial<FavoriteSite>) => {
    setFavorites((prev) =>
      prev.map((f) => (f.id === id ? { ...f, ...patch } : f))
    );
  }, []);

  const deleteFavorite = useCallback((id: string) => {
    setFavorites((prev) => prev.filter((f) => f.id !== id));
  }, []);

  const resetDefaults = useCallback(() => {
    setFavorites(DEFAULT_FAVORITES);
  }, []);

  return { favorites, addFavorite, updateFavorite, deleteFavorite, resetDefaults };
}
